import axios, { AxiosError } from "axios";

// Base URL — '/api' in production (nginx-proxied), local dev too (vite proxy).
export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? "/api",
  withCredentials: true,
  timeout: 30_000,
});

/**
 * X-Tenant-Override (2026-05-29): super_admin impersonation header.
 *
 * The chosen tenant slug is persisted in localStorage so the choice survives
 * page reloads. Switching/clearing is exposed as plain functions instead of a
 * Pinia store dependency — avoids a store↔client circular import.
 *
 * Backend behaviour:
 *   - Non-super_admin requesters get 403 if this header is set (the UI hides
 *     the dropdown for them, but defence-in-depth on the server).
 *   - Unknown slug → 400; the UI clears the override on receipt.
 */
const TENANT_OVERRIDE_LS_KEY = "cdp_tenant_override";

function readOverride(): string | null {
  try { return localStorage.getItem(TENANT_OVERRIDE_LS_KEY); } catch { return null; }
}

export function setTenantOverride(slug: string | null): void {
  try {
    if (slug) localStorage.setItem(TENANT_OVERRIDE_LS_KEY, slug);
    else localStorage.removeItem(TENANT_OVERRIDE_LS_KEY);
  } catch { /* localStorage disabled — single-tab only */ }
}

export function getTenantOverride(): string | null {
  return readOverride();
}

/** A6 / S3 — read the readable `cdp_csrf` cookie the engine set on login. */
function readCsrfCookie(): string | null {
  try {
    const m = document.cookie.match(/(?:^|;\s*)cdp_csrf=([^;]+)/);
    return m ? decodeURIComponent(m[1]!) : null;
  } catch {
    return null;
  }
}

const CSRF_SAFE_METHODS = new Set(["get", "head", "options"]);

apiClient.interceptors.request.use((cfg) => {
  cfg.headers = cfg.headers ?? {};
  const h = cfg.headers as Record<string, string>;
  // An EXPLICIT per-request X-Tenant-Override wins — e.g. a super_admin reading
  // a SPECIFIC tenant's data (editing tenant B in admin settings) regardless of
  // the active session override. Only fall back to the persisted session-wide
  // override when the caller didn't set one.
  if (!h["X-Tenant-Override"]) {
    const slug = readOverride();
    if (slug) h["X-Tenant-Override"] = slug;
  }
  // A6 / S3 — echo the CSRF cookie back as a header on mutating requests
  // (double-submit). No-op when the engine hasn't issued the cookie (flag off).
  const method = (cfg.method ?? "get").toLowerCase();
  if (!CSRF_SAFE_METHODS.has(method) && !h["X-CSRF-Token"]) {
    const csrf = readCsrfCookie();
    if (csrf) h["X-CSRF-Token"] = csrf;
  }
  return cfg;
});

/**
 * URLs the 401-redirect handler must NOT intercept — otherwise we'd loop
 * (login posting → 401 → redirect to /login → already there) or hide a
 * legitimate "credentials wrong" toast from the login form.
 */
const AUTH_EXEMPT_PATHS = ["/v1/auth/login", "/v1/auth/me", "/v1/auth/logout"];

/**
 * Должен ли 401 увести на /login. Чистая (тестируемая) функция.
 *
 * НЕ уводим, когда текущий роут — публичная bare-страница (опрос /s, центр
 * предпочтений /p, QR /q, реферал /r, сам /login): аноним там легитимно без
 * сессии, а ФОНОВЫЙ 401 сервисного виджета топ-бара (напр. /v1/ai/cost-summary)
 * не должен выкидывать получателя публичной ссылки на форму входа (баг: страница
 * опроса редиректилась на /login).
 */
export function shouldRedirectToLogin(opts: {
  status: number; url: string; routeName?: unknown; routeBare?: boolean; pathname?: string;
}): boolean {
  if (opts.status !== 401) return false;
  if (AUTH_EXEMPT_PATHS.some((p) => opts.url.endsWith(p))) return false;
  if (opts.routeName === "login") return false;
  if (opts.routeBare === true) return false;
  // По РЕАЛЬНОМУ URL браузера: на app-init 401 может прилететь ДО готовности роутера
  // (currentRoute ещё в START_LOCATION "/"), поэтому routeBare там ещё false. Публичные
  // пути /s /p /q /r определяем по window.location.pathname — не зависит от гонки роутера.
  if (opts.pathname && /^\/(s|p|q|r)\//.test(opts.pathname)) return false;
  return true;
}

/**
 * One-shot redirect guard — prevents N parallel 401s (Dashboard fires 4 GETs
 * in parallel) from each triggering their own router.push and stacking history.
 */
let redirecting = false;
// Самолечение залипшего tenant-override: сброшен ли уже (чтобы не зациклить reload).
let overrideHealed = false;

/**
 * Нужно ли сбросить залипший tenant-override. Чистая (тестируемая) функция: override бьёт КАЖДЫЙ
 * запрос → вход «залипает». Лечим, когда сервер отбил override по роли (403) ИЛИ слаг устарел (400).
 */
export function shouldHealTenantOverride(status: number, code: string | undefined, hasOverride: boolean): boolean {
  if (!hasOverride) return false;
  return (status === 403 && code === "TENANT_OVERRIDE_FORBIDDEN") || (status === 400 && code === "TENANT_NOT_FOUND");
}

// Global error normalization + auto-redirect to /login on 401 for any
// non-auth endpoint. Background: when the user comes back after their cookie
// TTL has elapsed (or after the engine has been restarted with a different
// JWT secret), every dashboard fetch returns 401. Without this interceptor
// the user just saw "Ошибка UNAUTHENTICATED" stripes and had to manually
// clear state. With it, they land on /login transparently.
apiClient.interceptors.response.use(
  (r) => r,
  (err: AxiosError<{ error?: string; message?: string }>) => {
    const status = err.response?.status ?? 0;
    const url = (err.config?.url ?? "").toString();
    const isAuthExempt = AUTH_EXEMPT_PATHS.some((p) => url.endsWith(p));

    if (status === 401 && !isAuthExempt && !redirecting) {
      redirecting = true;
      // Lazy import — `client.ts` is itself imported by stores/auth, so a
      // static import would create a cycle. Dynamic import breaks it.
      Promise.all([
        import("../stores/auth"),
        import("../router"),
      ]).then(([authMod, routerMod]) => {
        try { authMod.useAuthStore().$reset?.(); } catch {/* ignore */}
        const cur = routerMod.router.currentRoute.value;
        const pathname = typeof window !== "undefined" ? window.location.pathname : undefined;
        if (shouldRedirectToLogin({ status, url, routeName: cur.name, routeBare: cur.meta?.bare === true, pathname })) {
          routerMod.router.push({
            name: "login",
            query: { redirect: cur.fullPath, expired: "1" },
          }).finally(() => { redirecting = false; });
        } else {
          redirecting = false;
        }
      }).catch(() => { redirecting = false; });
    }

    const data = err.response?.data;

    // Залипший tenant-override (напр. остался от super_admin-сессии, а вошли под НЕ-super_admin,
    // либо слаг устарел) бьёт КАЖДЫЙ запрос с заголовком → вход «залипает» без выхода из UI.
    // Самолечение: сбросить override и один раз перезагрузиться (дальше заголовок не шлётся).
    const errCode = (data as { error?: string } | undefined)?.error;
    if (!overrideHealed && shouldHealTenantOverride(status, errCode, !!readOverride())) {
      overrideHealed = true;
      setTenantOverride(null);
      if (typeof window !== "undefined") window.location.reload();
    }

    const msg =
      data?.message ||
      data?.error ||
      err.message ||
      "Неизвестная ошибка";
    return Promise.reject(
      new ApiError(msg, status, data?.error, err, data),
    );
  },
);

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string | undefined,
    public readonly cause: unknown,
    /** Parsed JSON body from the failed response (when available). */
    public readonly payload?: Record<string, unknown> | unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}
